/* ==========================================================================
   Snapzee — motion + brief form. Vanilla, no dependencies.

   Everything degrades: with JS off the page is a normal, readable, fully
   submittable long-form page. JS only adds depth, reveals and the step flow.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------- start at the top */
  // A reload keeps whatever hash the address bar picked up from the last link
  // click, so the page reopens halfway down. On a reload we clear the hash and
  // go to the top; a fresh visit to a #brief link is still honoured.
  (function () {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    var reloaded = false;
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      reloaded = nav ? nav.type === 'reload'
                     : !!(performance.navigation && performance.navigation.type === 1);
    } catch (e) { /* older browsers: leave it alone */ }
    if (!reloaded) return;

    if (location.hash) {
      history.replaceState(null, '', location.pathname + location.search);
    }

    // Jump, don't glide — smooth scrolling here reads as a glitch.
    function toTop() {
      var html = document.documentElement;
      var prev = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      window.scrollTo(0, 0);
      html.style.scrollBehavior = prev;
    }
    toTop();
    window.addEventListener('load', toTop);
  })();

  /* ---------------------------------------------------------------- utils */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  // Document-relative top. We never read getBoundingClientRect on elements we
  // transform — that would feed our own translate back into the next frame.
  function docTop(el) {
    var y = 0;
    for (var n = el; n; n = n.offsetParent) y += n.offsetTop;
    return y;
  }

  /* ------------------------------------------------------- scroll engine */
  // One rAF loop, one passive scroll listener, all readers registered here.

  var readers = [];
  var ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      var y = window.pageYOffset;
      var vh = window.innerHeight;
      try {
        for (var i = 0; i < readers.length; i++) readers[i](y, vh);
      } finally {
        // Always release the latch. One misbehaving reader must not be able to
        // wedge `ticking` and kill every scroll effect on the page.
        ticking = false;
      }
    });
  }

  var measurers = [];
  function remeasure() { for (var i = 0; i < measurers.length; i++) measurers[i](); onScroll(); }

  /* ------------------------------------------------------------- reveals */

  function initReveals(root) {
    var items = $$('[data-reveal]', root);
    if (!items.length) return;

    if (!('IntersectionObserver' in window) || REDUCED) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    // Stagger siblings so groups cascade instead of popping together.
    var seen = new Map();
    items.forEach(function (el) {
      var p = el.parentElement;
      var n = seen.get(p) || 0;
      seen.set(p, n + 1);
      if (n) el.style.setProperty('--d', (n * 70) + 'ms');
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

    items.forEach(function (el) { io.observe(el); });

    // Safety net: whatever happens with observer timing, nothing that is
    // already on screen stays invisible.
    function sweep() {
      items.forEach(function (el) {
        if (el.classList.contains('is-in')) return;
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.classList.add('is-in');
          io.unobserve(el);
        }
      });
    }
    window.addEventListener('load', function () { setTimeout(sweep, 300); });
    setTimeout(sweep, 1600);
  }

  /* ------------------------------------------------------------ parallax */
  // data-parallax="0.14" → element drifts against the scroll by that factor.

  function initParallax(root) {
    if (REDUCED) return;
    var els = $$('[data-parallax], [data-parallax-x]', root);
    if (!els.length) return;

    var items = els.map(function (el) {
      return {
        el: el,
        speed: parseFloat(el.getAttribute('data-parallax')) || 0,
        sx: parseFloat(el.getAttribute('data-parallax-x')) || 0,
        top: 0, h: 0
      };
    });

    function measure() {
      items.forEach(function (it) {
        it.el.style.setProperty('--py', '0px');   // measure untransformed
        it.top = docTop(it.el);
        it.h = it.el.offsetHeight;
      });
    }

    readers.push(function (y, vh) {
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var mid = it.top + it.h / 2;
        var delta = (y + vh / 2) - mid;
        // Only animate while roughly on screen — keeps the loop cheap.
        if (Math.abs(delta) > vh * 1.6 + it.h) continue;
        if (it.speed) it.el.style.setProperty('--py', (delta * it.speed).toFixed(2) + 'px');
        if (it.sx) it.el.style.setProperty('--px', (delta * it.sx).toFixed(2) + 'px');
      }
    });

    measurers.push(measure);
    measure();
  }

  /* -------------------------------------------------------- pointer tilt */
  // Hero stack drifts with the cursor. Depth per card = layered feel.

  function initTilt(root) {
    if (REDUCED || !window.matchMedia('(hover: hover)').matches) return;
    var scope = $('[data-tilt]', root);
    if (!scope) return;

    var layers = $$('[data-depth]', scope).map(function (el) {
      return { el: el, d: parseFloat(el.getAttribute('data-depth')) || 0 };
    });
    if (!layers.length) return;

    var tx = 0, ty = 0, cx = 0, cy = 0, running = false;

    function frame() {
      cx += (tx - cx) * 0.075;
      cy += (ty - cy) * 0.075;
      layers.forEach(function (l) {
        l.el.style.setProperty('--px', (cx * l.d).toFixed(2) + 'px');
        l.el.style.setProperty('--sy', (cy * l.d).toFixed(2) + 'px');
      });
      if (Math.abs(tx - cx) > 0.05 || Math.abs(ty - cy) > 0.05) requestAnimationFrame(frame);
      else running = false;
    }

    function kick() { if (!running) { running = true; requestAnimationFrame(frame); } }

    scope.addEventListener('pointermove', function (e) {
      var r = scope.getBoundingClientRect();
      tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
      kick();
    }, { passive: true });

    scope.addEventListener('pointerleave', function () { tx = 0; ty = 0; kick(); }, { passive: true });
  }

  /* ------------------------------------------------------------ the brief */

  var PROVIDERS = [
    { re: /(drive|docs)\.google\.com|photos\.app\.goo\.gl|photos\.google\.com/i, name: 'Google Drive' },
    { re: /dropbox\.com/i, name: 'Dropbox' },
    { re: /1drv\.ms|onedrive\.live\.com|sharepoint\.com/i, name: 'OneDrive' },
    { re: /icloud\.com/i, name: 'iCloud' },
    { re: /we\.tl|wetransfer\.com/i, name: 'WeTransfer' },
    { re: /mega\.nz|mega\.io/i, name: 'MEGA' },
    { re: /pcloud\.link|pcloud\.com/i, name: 'pCloud' },
    { re: /amazon\.com\/photos|amazon\.[a-z.]+\/drive/i, name: 'Amazon Photos' }
  ];

  function detectProvider(url) {
    for (var i = 0; i < PROVIDERS.length; i++) if (PROVIDERS[i].re.test(url)) return PROVIDERS[i].name;
    return null;
  }

  function initForm(root) {
    // Shopify renders the <form> itself via {% form 'contact' %}, so we match
    // on the class it is given rather than a data attribute we control.
    var form = $('form.sz-form', root) || $('[data-brief]', root);
    if (!form) return;

    /* --- cloud link recognition ------------------------------------- */
    var link = $('[data-link]', form);
    var badge = $('[data-provider]', form);

    if (link && badge) {
      var setBadge = function () {
        var v = link.value.trim();
        badge.classList.remove('is-known', 'is-bad');
        if (!v) { badge.innerHTML = '<i></i>Awaiting link'; link.setCustomValidity(''); return; }

        var ok = /^https?:\/\/.+\..+/i.test(v);
        var name = ok ? detectProvider(v) : null;

        if (!ok) {
          badge.classList.add('is-bad');
          badge.innerHTML = '<i></i>Not a link yet';
          link.setCustomValidity('Please paste a full link starting with https://');
        } else if (name) {
          badge.classList.add('is-known');
          badge.innerHTML = '<i></i>' + name;
          link.setCustomValidity('');
        } else {
          badge.innerHTML = '<i></i>Link detected';
          link.setCustomValidity('');
        }
        syncColophon();
      };
      link.addEventListener('input', setBadge);
      link.addEventListener('blur', setBadge);
      setBadge();
    }

    /* --- live colophon ---------------------------------------------- */
    var colo = $('[data-colophon]', form.closest('.sz-brief') || root);

    function fieldLabel(name) {
      var checked = form.querySelector('input[name="' + name + '"]:checked');
      if (checked) return (checked.getAttribute('data-label') || checked.value).trim();
      var el = form.elements[name];
      if (!el) return '';
      var v = (el.value || '').trim();
      if (el.tagName === 'SELECT' && el.selectedIndex > -1 && v) return el.options[el.selectedIndex].text.trim();
      return v;
    }

    function syncColophon() {
      if (!colo) return;
      var any = false;
      $$('[data-slot]', colo).forEach(function (dd) {
        var v = fieldLabel(dd.getAttribute('data-slot'));
        var empty = !v;
        if (!empty) any = true;
        dd.textContent = empty ? (dd.getAttribute('data-empty') || '—') : v;
        dd.classList.toggle('is-empty', empty);
      });
      // A phone shows the summary only once it has something to summarise —
      // five rows of "Not set yet" ahead of the first question is 340px of
      // nothing. See §12 in the stylesheet.
      colo.classList.toggle('has-values', any);
    }

    form.addEventListener('input', syncColophon);
    form.addEventListener('change', syncColophon);
    syncColophon();

    /* --- stepped flow ------------------------------------------------ */
    var steps = $$('[data-form-step]', form);
    if (steps.length < 2) return;

    // Steps hide fields with display:none, and the browser refuses to focus a
    // hidden invalid control — so we take validation over ourselves. Without
    // JS the form stays one long page and native validation still applies.
    form.classList.add('sz-form--stepped');
    form.noValidate = true;
    var segs = $$('[data-seg]', root);
    var live = $('[data-live]', form);
    var at = 0;

    function paint() {
      steps.forEach(function (s, i) { s.classList.toggle('is-on', i === at); });
      segs.forEach(function (s, i) {
        s.classList.toggle('is-done', i < at);
        s.classList.toggle('is-current', i === at);
      });
      if (live) live.textContent = 'Step ' + (at + 1) + ' of ' + steps.length;
    }

    function valid(i) {
      var fields = $$('input, select, textarea', steps[i]);
      for (var n = 0; n < fields.length; n++) {
        if (!fields[n].checkValidity()) { fields[n].reportValidity(); return false; }
      }
      return true;
    }

    function go(i, scroll) {
      at = clamp(i, 0, steps.length - 1);
      paint();
      if (scroll) {
        var box = form.closest('.sz-brief') || form;
        var y = docTop(box) - 90;
        window.scrollTo({ top: y, behavior: REDUCED ? 'auto' : 'smooth' });
      }
    }

    form.addEventListener('click', function (e) {
      var next = e.target.closest('[data-next]');
      var back = e.target.closest('[data-back]');
      if (next) { e.preventDefault(); if (valid(at)) go(at + 1, true); }
      if (back) { e.preventDefault(); go(at - 1, true); }
    });

    // Enter should advance, not submit, until the last step.
    form.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || e.target.tagName === 'TEXTAREA') return;
      if (at < steps.length - 1) { e.preventDefault(); if (valid(at)) go(at + 1, true); }
    });

    // If the server bounced us back with errors, land on the step that has them.
    var bad = form.querySelector('[data-form-step] :invalid');
    if (bad) {
      var host = bad.closest('[data-form-step]');
      at = Math.max(0, steps.indexOf(host));
    }
    paint();
  }

  /* ---------------------------------------------------------- header/menu */

  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    if (!header.dataset.szBound) {
      header.dataset.szBound = '1';

      var burger = $('[data-burger]', header);
      var menu = $('[data-menu]');

      var close = function () {
        document.body.classList.remove('sz-menu-open');
        if (burger) burger.setAttribute('aria-expanded', 'false');
      };

      if (burger && menu) {
        burger.addEventListener('click', function () {
          var open = document.body.classList.toggle('sz-menu-open');
          burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        menu.addEventListener('click', function (e) { if (e.target.closest('a')) close(); });
        document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
      }
    }

    // Registered every init: the readers list is cleared when the theme editor
    // reloads a section, so this has to be re-added each time.
    // Only paint the bar once we're off the very top, so the hero stays clean.
    readers.push(function (y) { header.classList.toggle('is-stuck', y > 12); });
  }

  /* ------------------------------------------------------- quantity input */

  function initQty(root) {
    $$('[data-qty]', root).forEach(function (box) {
      if (box.dataset.szBound) return;
      box.dataset.szBound = '1';
      var input = $('input', box);
      if (!input) return;

      // Where the counter bottoms out belongs to the input, not to this code.
      // A cart line stops at zero — that is how a line is removed — while the
      // product form stops at one. Hard-coding one floor for both is what made
      // the minus refuse to empty a line.
      var floor = parseInt(input.getAttribute('min'), 10);
      if (isNaN(floor)) floor = 1;
      var minus = $('[data-step="-1"]', box);

      function reflect() {
        if (minus) minus.disabled = (parseInt(input.value, 10) || floor) <= floor;
      }

      box.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-step]');
        if (!btn) return;
        e.preventDefault();
        var at = parseInt(input.value, 10);
        if (isNaN(at)) at = floor;
        input.value = Math.max(floor, at + parseInt(btn.getAttribute('data-step'), 10));
        reflect();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      input.addEventListener('input', reflect);
      reflect();
    });
  }

  /* -------------------------------------------------------------- the cart */
  // A quantity typed into the cart is not a quantity until the form has been
  // posted. Leaving that to an "Update cart" click is what makes a line look
  // unremovable: you take it to zero, nothing moves, and the cart reads as
  // stuck. So a quantity change posts itself — after a pause, so three taps on
  // + are one round trip rather than three.

  function initCart(root) {
    $$('[data-cart]', root).forEach(function (box) {
      if (box.dataset.szBound) return;
      box.dataset.szBound = '1';

      var form = $('form', box);
      if (!form) return;
      var apply = $('[data-cart-update]', box);
      var timer = null;

      form.addEventListener('change', function (e) {
        if (!e.target.matches || !e.target.matches('input[name="updates[]"]')) return;
        clearTimeout(timer);
        box.classList.add('is-saving');
        timer = setTimeout(function () {
          if (apply) apply.click(); else form.submit();
        }, 550);
      });
    });
  }

  /* --------------------------------------------------------- social wall */
  // The wall is plain CSS grid — it needs no JS to lay out or to scroll. This
  // only adds muted hover-play for tiles that carry a video; the drift on the
  // photographs is handled by the parallax engine above, via data-parallax.

  function initSocial(root) {
    if (REDUCED) return;

    $$('[data-post-video]', root).forEach(function (vid) {
      if (vid.dataset.szBound) return;
      vid.dataset.szBound = '1';

      var tile = vid.closest('.sz-post');
      if (!tile) return;

      function play() {
        var p = vid.play();
        if (p && p.catch) p.catch(function () { /* blocked: the poster stays */ });
        tile.classList.add('is-playing');
      }
      function stop() {
        vid.pause();
        vid.currentTime = 0;
        tile.classList.remove('is-playing');
      }

      tile.addEventListener('mouseenter', play);
      tile.addEventListener('mouseleave', stop);
      tile.addEventListener('focus', play);
      tile.addEventListener('blur', stop);
    });
  }

  /* ----------------------------------------------------------- product page */
  // Two jobs: swap the photograph on the stage, and resolve the option chips
  // to a variant. Both are progressive — the stage shows its first frame and
  // the plain <select> submits the right variant with no JS at all.

  // Paper stock. The variant's own words decide it, so any product whose
  // option mentions board / card / normal gets the right physics without
  // being configured. Anything else leaves the book at its default feel.
  function stockFrom(text) {
    var t = String(text || '').toLowerCase();
    if (t.indexOf('board') !== -1) return 'board';
    if (t.indexOf('card') !== -1) return 'card';
    if (t.indexOf('normal') !== -1 || t.indexOf('standard') !== -1) return 'normal';
    return '';
  }

  function paintStock(text) {
    var stock = stockFrom(text);
    $$('[data-flipbook]').forEach(function (book) {
      if (stock) book.setAttribute('data-stock', stock);
      else book.removeAttribute('data-stock');
    });
  }

  // How long a page of this stock takes to go over, read from the CSS so the
  // two never drift apart.
  function turnMs(book) {
    var raw = getComputedStyle(book).getPropertyValue('--turn-ms').trim();
    var n = parseFloat(raw);
    if (!n) return 1100;
    return (raw.indexOf('ms') === -1 ? n * 1000 : n) + 60;
  }

  function initFlipbook(root) {
    $$('[data-flipbook]', root).forEach(function (book) {
      if (book.dataset.szBound) return;
      book.dataset.szBound = '1';

      var leaves = $$('[data-leaf]', book);
      if (!leaves.length) return;

      // The book takes its shape from the pages inside it. A 2:3 magazine and
      // a square album are not the same object, and forcing both into one
      // spread ratio makes object-fit trim the top and bottom off every page.
      // Read the ratio off the first page — from the width/height attributes
      // first, so the book is the right shape before anything has loaded.
      (function shapeBook() {
        var img = book.querySelector('.sz-pagemat img, .sz-cover__frame img, .sz-cover--photo img');
        if (!img) return;
        function set(w, h) {
          if (!w || !h) return false;
          var ar = w / h;
          if (ar < 0.2 || ar > 5) return false;      // nonsense, leave the default
          book.style.setProperty('--page-ar', ar.toFixed(4));
          // The wall of pages further down is made of the same pages, so it
          // wants the same shape. Set it at the root and everything inherits.
          document.documentElement.style.setProperty('--page-ar', ar.toFixed(4));
          return true;
        }
        set(parseInt(img.getAttribute('width'), 10), parseInt(img.getAttribute('height'), 10));
        if (img.complete) set(img.naturalWidth, img.naturalHeight);
        else img.addEventListener('load', function () {
          set(img.naturalWidth, img.naturalHeight);
        }, { once: true });
      })();

      var nav = book.parentElement.querySelector('[data-flipnav]');
      var count = nav ? $('[data-flipcount]', nav) : null;
      var hint = nav ? $('[data-fliphint]', nav) : null;
      var btns = nav ? $$('[data-turn]', nav) : [];

      // State is how many leaves have been turned. 0 is the shut front cover,
      // leaves.length is the shut back cover, and everything between is an
      // open spread — which is why the count reads one fewer than the leaves.
      var turned = 0;
      var spreads = leaves.length - 1;

      if (nav) nav.hidden = false;

      function paint() {
        leaves.forEach(function (leaf, i) {
          var isTurned = i < turned;
          leaf.classList.toggle('is-turned', isTurned);
          // Turned leaves stack up on the left, untouched ones on the right;
          // in both piles the one nearest the reader sits on top.
          leaf.style.zIndex = isTurned ? 200 + i : 200 - i;
        });

        var shut = turned === 0;
        book.classList.toggle('is-closed', shut);

        if (count) {
          count.textContent = shut ? 'Cover' : 'Spread ' + turned + ' / ' + spreads;
        }
        if (hint) {
          var next = shut ? hint.getAttribute('data-hint-shut')
                          : hint.getAttribute('data-hint-open');
          if (next) hint.textContent = next;
        }
        btns.forEach(function (b) {
          var back = b.getAttribute('data-turn') === '-1';
          // The last leaf never turns: its front is the final page, so the
          // book never ends face-down on a bare base.
          b.disabled = back ? turned === 0 : turned >= spreads;
        });
      }

      function go(dir) {
        var next = turned + dir;
        if (next < 0 || next > spreads) return;
        // The leaf actually in motion is the one being turned either way.
        var moving = leaves[dir > 0 ? turned : next];
        turned = next;
        paint();
        // While it swings, the moving leaf rides above both piles; when it
        // lands, paint() puts it back in its pile.
        if (moving && !REDUCED) {
          // Direction matters: the physics keyframes for lifting a page off
          // the right pile are the mirror of dropping it back.
          moving.classList.add('is-turning', dir > 0 ? 'is-fwd' : 'is-back');
          moving.style.zIndex = 500;
          setTimeout(function () {
            moving.classList.remove('is-turning', 'is-fwd', 'is-back');
            paint();
          }, turnMs(book));
        }
      }

      btns.forEach(function (b) {
        b.addEventListener('click', function () { go(parseInt(b.getAttribute('data-turn'), 10)); });
      });

      // Clicking the book itself turns it: right half forward, left half back.
      book.addEventListener('click', function (e) {
        // Shut, the whole cover opens it; shut on the back cover, it closes
        // back the other way. Open, the halves do what halves do.
        if (turned === 0) { go(1); return; }
        var r = book.getBoundingClientRect();
        go(e.clientX - r.left > r.width / 2 ? 1 : -1);
      });

      // Arrow keys once the book has been used.
      book.setAttribute('tabindex', '0');
      book.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); go(-1); }
      });

      paint();
    });
  }

  /* ----------------------------------------------------------- the ribbon */
  // The marker takes the colour of the cover being chosen, so the option and
  // the object on screen are never telling different stories.

  var RIBBON = ['#E86B5F', '#2B3A5C', '#F2B33D', '#4FB3A5', '#F3A0A0', '#7C4AC2'];

  function initRibbon(root) {
    var ribbon = $('[data-ribbon]', root);
    var chips = $('[data-options]', root);
    if (!ribbon || !chips) return;

    function tint() {
      var first = $$('input[data-option="0"]', chips);
      var pick = 0;
      first.forEach(function (i, n) { if (i.checked) pick = n; });
      ribbon.style.setProperty('--ribbon', RIBBON[pick % RIBBON.length]);
    }

    chips.addEventListener('change', tint);
    tint();
  }

  function initVariants(root) {
    $$('[data-pdp]', root).forEach(function (pdp) {
      if (pdp.dataset.szBound) return;
      pdp.dataset.szBound = '1';

      var chips = $('[data-options]', pdp);
      var fallback = $('[data-fallback]', pdp);
      var hidden = $('[data-variant-id]', pdp);
      var priceEl = $('[data-price]', pdp);
      var addBtn = $('[data-add]', pdp);
      var dataEl = $('[data-variant-data]');
      if (!chips || !hidden || !dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
      if (!variants || !variants.length) return;

      // Only now is it safe to trade the select for the chips.
      chips.hidden = false;
      if (fallback) fallback.remove();

      var addLabel = addBtn ? addBtn.innerHTML : '';

      function selected() {
        return $$('input[data-option]', chips)
          .filter(function (i) { return i.checked; })
          .map(function (i) { return i.value; });
      }

      function apply() {
        var want = selected();
        var match = null;
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i], ok = true;
          for (var j = 0; j < want.length; j++) {
            if (v.options[j] !== want[j]) { ok = false; break; }
          }
          if (ok) { match = v; break; }
        }
        if (!match) return;

        hidden.value = match.id;

        // The book is made of whatever paper they just picked.
        paintStock(match.title || match.options.join(' '));

        if (priceEl) {
          var html = match.price;
          if (match.on_sale) {
            html += ' <del>' + match.compare + '</del>' +
                    ' <span class="sz-badge">Save ' + match.save + '</span>';
          }
          priceEl.innerHTML = html;
        }

        if (addBtn) {
          addBtn.disabled = !match.available;
          addBtn.innerHTML = match.available ? addLabel : 'Sold out';
        }

        // The dock carries the same numbers, so the two never disagree.
        var dockPrice = $('[data-dock-price]');
        var dockVariant = $('[data-dock-variant]');
        var dockAdd = $('[data-dock-add]');
        if (dockPrice) dockPrice.textContent = match.price;
        if (dockVariant) dockVariant.textContent = match.title || '';
        if (dockAdd) {
          dockAdd.disabled = !match.available;
          dockAdd.textContent = match.available ? dockAdd.textContent : 'Sold out';
        }

        // Anything that wants to follow the choice — the gallery, for one —
        // listens rather than being reached into from here.
        document.dispatchEvent(new CustomEvent('sz:variant', { detail: match }));

        // Keep the address bar honest, so a shared link opens the same variant.
        if (history.replaceState) {
          try {
            var u = new URL(window.location.href);
            u.searchParams.set('variant', match.id);
            history.replaceState({}, '', u);
          } catch (e) { /* file:// and sandboxed frames refuse this */ }
        }
      }

      chips.addEventListener('change', function (e) {
        if (e.target.matches('input[data-option]')) apply();
      });

      // The book is already made of something when the page opens — set the
      // paper without running the rest of apply(), which would rewrite the URL.
      paintStock(selected().join(' '));
    });
  }

  /* -------------------------------------------------------------- buy dock */
  // Rides up once the opening has scrolled away. It submits the same form as
  // the button up top (via form="add-to-cart"), so there is one source of
  // truth for the variant and the quantity.

  function initDock(root) {
    var dock = $('[data-dock]', root);
    var opening = $('.sz-open', root);
    if (!dock || !opening) return;
    if (dock.dataset.szBound) return;
    dock.dataset.szBound = '1';

    dock.hidden = false;

    readers.push(function (y) {
      var past = y > docTop(opening) + opening.offsetHeight - 120;
      dock.classList.toggle('is-up', past);
    });
  }

  /* ----------------------------------------------------- collection sorting */
  // The sort control is a real GET form; JS just saves the extra click.

  function initSort(root) {
    $$('[data-autosubmit]', root).forEach(function (form) {
      if (form.dataset.szBound) return;
      form.dataset.szBound = '1';
      form.addEventListener('change', function () { form.submit(); });
    });
  }


  /* --------------------------------------------------------- the matchmaker */
  // Five questions scored against product tags, all client-side. The section
  // prints the catalogue as JSON; weights ride on each step's data-weight.
  // Budget is rank-based: the catalogue is split into thirds by price, so the
  // bands stay honest no matter what the products cost.

  function initQuiz(root) {
    $$('[data-quiz]', root).forEach(function (quiz) {
      if (quiz.dataset.szBound) return;
      quiz.dataset.szBound = '1';

      var data = quiz.querySelector('[data-quiz-products]');
      var products = [];
      try { products = JSON.parse(data.textContent); } catch (e) { return; }
      if (!products.length) return;

      // price rank → band per product: cheapest third, middle, dearest third
      var byPrice = products.slice().sort(function (a, b) { return a.price - b.price; });
      byPrice.forEach(function (p, i) {
        var q = byPrice.length > 1 ? i / (byPrice.length - 1) : 0.5;
        p.band = q < 1 / 3 ? 'low' : q > 2 / 3 ? 'high' : 'mid';
      });

      var steps = $$('[data-quiz-step]', quiz);
      var film = $$('.sz-quiz__film li', quiz);
      var results = quiz.querySelector('[data-quiz-results]');
      var topBox = quiz.querySelector('[data-quiz-top]');
      var moreBox = quiz.querySelector('[data-quiz-more]');
      var moreHead = quiz.querySelector('.sz-quiz__morehead');
      var back = quiz.querySelector('[data-quiz-back]');
      var count = quiz.querySelector('[data-quiz-count]');
      var kicker = quiz.querySelector('[data-quiz-kicker]');
      var answers = [];   // one per completed step: {tags:[], band:'', weight:n}
      var at = 0;

      // Column counts that never leave one lonely tile on its own row.
      var COLS = { 2: 2, 3: 3, 4: 4, 5: 3, 6: 3, 7: 4, 8: 4 };

      function pickedTags(upto) {
        var out = [];
        for (var i = 0; i < upto && i < answers.length; i++) {
          if (answers[i]) out = out.concat(answers[i].tags);
        }
        return out;
      }

      // Drop the answers that can't follow from what's already been said —
      // no "romantic" once the album is for a friend. Each option carries a
      // data-not veto list; vetoes only, so a skipped question narrows
      // nothing. Returns how many options are left standing.
      function refine(step, idx) {
        var grid = step.querySelector('.sz-quiz__opts');
        if (!grid) return 99;
        var opts = $$('[data-quiz-opt]', grid);
        var picked = pickedTags(idx);
        var live = [];

        opts.forEach(function (o) {
          var veto = (o.getAttribute('data-not') || '').split(' ').filter(Boolean);
          var out = veto.some(function (t) { return picked.indexOf(t) !== -1; });
          o.hidden = out;
          if (out) o.classList.remove('is-picked');
          else live.push(o);
        });

        // Only conditional grids get their columns re-counted; the fixed
        // two-up screens keep the width their class asks for.
        if (opts.some(function (o) { return o.hasAttribute('data-not'); })) {
          grid.style.setProperty('--cols', COLS[live.length] || 4);
        }

        // Card colours follow the visible order, not the markup order, so a
        // filtered screen still cycles cream / mint / blush / white / gold.
        live.forEach(function (o, k) {
          o.setAttribute('data-tone', (k % 5) + 1);
          o.classList.toggle('is-lonely', live.length % 2 === 1 && k === live.length - 1);
        });

        return live.length;
      }

      // Walk in `dir` until a step still has a real choice to offer. A step
      // filtered down to one answer isn't a question, so it answers itself.
      function resolve(i, dir) {
        while (i >= 0 && i < steps.length) {
          if (refine(steps[i], i) >= 2) return i;
          answers[i] = { tags: [], band: '', weight: 0 };
          i += dir;
        }
        return i < 0 ? 0 : steps.length;
      }

      function render(i) {
        at = i;
        steps.forEach(function (s, k) { s.hidden = k !== i || i >= steps.length; });
        results.hidden = i < steps.length;
        film.forEach(function (f, k) { f.classList.toggle('is-done', k < answers.length); });
        back.hidden = i === 0;
        if (count) {
          count.hidden = i >= steps.length;
          count.textContent = (i + 1) + ' / ' + steps.length;
        }
      }

      function go(i, dir) {
        var target = resolve(i, dir || 1);
        if (target >= steps.length) { finish(); return; }
        render(target);
        keepInView();
      }

      // Screens shrink as options get filtered out, so the card can end up
      // scrolled past its own top. Nudge it back only when it actually is.
      function keepInView() {
        var card = quiz.querySelector('.sz-quiz__card');
        if (!card) return;
        var top = card.getBoundingClientRect().top;
        if (top > 8 && top < window.innerHeight * 0.5) return;
        window.scrollTo({
          top: window.pageYOffset + top - 88,
          behavior: REDUCED ? 'auto' : 'smooth'
        });
      }

      function tone(i) { return 'abcde'.charAt(i % 5); }

      function card(p, i, big) {
        var a = document.createElement('a');
        a.className = big ? 'sz-quiz__win' : 'sz-quiz__alt';
        a.href = p.url;
        var media = p.img
          ? '<img src="' + p.img + '" alt="" loading="lazy">'
          : '<span class="sz-ph sz-ph--' + tone(i) + '"></span>';
        a.innerHTML =
          '<span class="sz-quiz__winmedia">' + media + '</span>' +
          '<span class="sz-quiz__winbase"><b>' + p.title + '</b>' +
          (p.type ? '<i>' + p.type + '</i>' : '') +
          '<em>' + p.priceLabel + '</em></span>' +
          (big ? '<span class="sz-quiz__winflag">Perfect match</span>' : '');
        return a;
      }

      function finish() {
        var scored = products.map(function (p) {
          var score = 0;
          answers.forEach(function (ans) {
            if (ans.band && ans.band === p.band) score += ans.weight;
            ans.tags.forEach(function (t) {
              if (p.tags.indexOf(t) !== -1) score += ans.weight;
            });
          });
          return { p: p, score: score };
        }).sort(function (a, b) { return b.score - a.score; });

        // Nothing scored: either the answers are unusual or the catalogue
        // isn't tagged yet. Either way, don't stamp "Perfect match" on a
        // product the quiz never actually chose — say so and lead with the
        // chat instead.
        var matched = scored[0].score > 0;
        results.classList.toggle('is-nomatch', !matched);
        if (kicker) kicker.textContent = matched
          ? (results.getAttribute('data-kicker-match') || kicker.textContent)
          : (results.getAttribute('data-kicker-none') || kicker.textContent);

        topBox.textContent = '';
        moreBox.textContent = '';
        var rest;
        if (matched) {
          topBox.appendChild(card(scored[0].p, 0, true));
          rest = scored.slice(1, 4);
        } else {
          rest = scored.slice(0, 3);
        }
        if (moreHead) {
          moreHead.hidden = !rest.length;
          moreHead.textContent = matched
            ? (results.getAttribute('data-more-match') || moreHead.textContent)
            : (results.getAttribute('data-more-none') || moreHead.textContent);
        }
        rest.forEach(function (s, i) { moreBox.appendChild(card(s.p, i + 1, false)); });
        render(steps.length);
        results.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      steps.forEach(function (step, si) {
        var weight = parseInt(step.getAttribute('data-weight'), 10) || 1;
        $$('[data-quiz-opt]', step).forEach(function (opt) {
          opt.addEventListener('click', function () {
            $$('[data-quiz-opt]', step).forEach(function (o) { o.classList.remove('is-picked'); });
            opt.classList.add('is-picked');
            answers[si] = {
              tags: (opt.getAttribute('data-tags') || '').split(' ').filter(Boolean),
              band: opt.getAttribute('data-band') || '',
              weight: weight
            };
            answers.length = si + 1;
            // Anything chosen further down may no longer be on offer.
            steps.slice(si + 1).forEach(function (later) {
              $$('[data-quiz-opt]', later).forEach(function (o) { o.classList.remove('is-picked'); });
            });
            setTimeout(function () { go(si + 1, 1); }, 260);
          });
        });
      });

      back.addEventListener('click', function () {
        if (at > 0) { answers.length = at - 1; go(at - 1, -1); }
      });

      var again = quiz.querySelector('[data-quiz-restart]');
      if (again) again.addEventListener('click', function () {
        answers.length = 0;
        $$('[data-quiz-opt]', quiz).forEach(function (o) { o.classList.remove('is-picked'); });
        go(0, 1);
      });

      render(resolve(0, 1));
    });
  }

  /* ---------------------------------------------------------- the gallery */
  // The product stage while the flipbook is put away. One index drives
  // everything — the big photograph, the rail, the arrows, the counter and the
  // lightbox — so no two of them can disagree about which shot is showing.
  //
  // Everything beyond the thumbnails is drawn only once this has bound: the
  // arrows, the zoom chip and the lightbox ship hidden, so with no JS the page
  // offers nothing that does nothing.

  function initGallery(root) {
    $$('[data-gallery]', root).forEach(function (gal) {
      if (gal.dataset.szBound) return;

      var stage = $('[data-gallery-stage]', gal);
      var main = stage ? stage.querySelector('img') : null;
      if (!main) return;
      gal.dataset.szBound = '1';

      var picks = $$('[data-gallery-pick]', gal);
      var rail = $('[data-gallery-thumbs]', gal);
      var countEl = $('[data-gallery-count]', gal);
      var arrows = $$('[data-gallery-step]', gal);
      var zoom = $('[data-gallery-zoom]', gal);
      var at = Math.max(0, picks.map(function (p) {
        return p.classList.contains('is-on');
      }).indexOf(true));

      // A single photograph has nothing to move between, but it can still be
      // opened large.
      if (picks.length > 1) {
        arrows.forEach(function (b) { b.hidden = false; });
        if (countEl) countEl.hidden = false;
      }
      if (zoom) zoom.hidden = false;

      function fullOf(i) {
        var pick = picks[i];
        return pick ? pick.getAttribute('data-full') : main.currentSrc || main.src;
      }

      function altOf(i) {
        var pick = picks[i];
        var thumb = pick && pick.querySelector('img');
        return thumb && thumb.alt ? thumb.alt : main.alt;
      }

      function show(i, scrollRail) {
        if (!picks.length) return;
        // Wrapping is what an arrow at the end of a short set should do —
        // stopping dead reads as broken on a page with four photographs.
        at = (i % picks.length + picks.length) % picks.length;
        var full = fullOf(at);
        if (full) {
          // image_tag writes a srcset; leaving it would let the browser keep
          // serving the old photograph at the size it already picked.
          main.removeAttribute('srcset');
          main.removeAttribute('sizes');
          main.src = full;
        }
        main.alt = altOf(at);
        picks.forEach(function (o, n) { o.classList.toggle('is-on', n === at); });
        if (countEl) countEl.textContent = (at + 1) + ' / ' + picks.length;
        if (scrollRail && rail && picks[at] && rail.scrollWidth > rail.clientWidth + 4) {
          var t = picks[at];
          rail.scrollTo({
            left: t.offsetLeft - (rail.clientWidth - t.offsetWidth) / 2,
            behavior: prefersStill() ? 'auto' : 'smooth'
          });
        }
        if (box.isOpen()) box.paint(at);
      }

      picks.forEach(function (pick, i) {
        pick.addEventListener('click', function () { show(i, false); });
      });

      arrows.forEach(function (b) {
        b.addEventListener('click', function () {
          show(at + (parseInt(b.getAttribute('data-gallery-step'), 10) || 0), true);
        });
      });

      // The stage itself is a swipe surface on a phone, which is how a photo
      // set is expected to behave there.
      (function swipe(el, onMove) {
        var x0 = null, y0 = null;
        el.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'mouse') return;
          x0 = e.clientX; y0 = e.clientY;
        }, { passive: true });
        el.addEventListener('pointerup', function (e) {
          if (x0 === null) return;
          var dx = e.clientX - x0, dy = e.clientY - y0;
          x0 = null;
          if (Math.abs(dx) > 42 && Math.abs(dx) > Math.abs(dy)) onMove(dx < 0 ? 1 : -1);
        }, { passive: true });
        el.addEventListener('pointercancel', function () { x0 = null; }, { passive: true });
      })(stage, function (dir) { show(at + dir, true); });

      /* ------------------------------------------------------- the lightbox */
      // Frames, not thumbnails: a product with one photograph has no rail and
      // must still open large.
      var frames = picks.length
        ? picks.map(function (pick) {
            var thumb = pick.querySelector('img');
            return { src: pick.getAttribute('data-full'), alt: thumb && thumb.alt ? thumb.alt : '' };
          })
        : [{ src: main.currentSrc || main.src, alt: main.alt || '' }];

      var box = lightbox(frames, function () { return at; }, function (i) { show(i, true); });
      if (zoom) zoom.addEventListener('click', function () { box.open(at); });
      stage.addEventListener('click', function (e) {
        if (e.target.closest('button')) return;
        box.open(at);
      });

      /* -------------------------------------------- the variant's own photo */
      // Picking a variant that carries its own photograph moves the stage to
      // it. Variants with no photograph of their own leave the gallery alone
      // rather than snapping it back to the first shot.
      document.addEventListener('sz:variant', function (e) {
        var id = e.detail && e.detail.media_id;
        if (!id) return;
        for (var i = 0; i < picks.length; i++) {
          if (picks[i].getAttribute('data-media-id') === String(id)) { show(i, true); return; }
        }
      });

      show(at, false);
    });
  }

  function prefersStill() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // The photograph on a dark ground, at whatever shape it actually is. Arrow
  // keys and a swipe move through the set; Escape, the backdrop and the close
  // button all put it away, and focus goes back to where it came from.
  function lightbox(frames, getIndex, setIndex) {
    var el = $('[data-lightbox]');
    var img = el && $('[data-lightbox-img]', el);
    var dead = { open: function () {}, paint: function () {}, isOpen: function () { return false; } };
    if (!el || !img || !frames.length) return dead;

    var count = $('[data-lightbox-count]', el);
    var opener = null;
    var open = false;

    function paint(i) {
      var frame = frames[i];
      if (!frame) return;
      img.src = frame.src || '';
      img.alt = frame.alt || '';
      if (count) count.textContent = (i + 1) + ' / ' + frames.length;
    }

    // Moving inside the lightbox moves the page behind it too, so closing it
    // leaves the stage on the photograph that was being looked at.
    function move(i) {
      var n = (i % frames.length + frames.length) % frames.length;
      paint(n);
      setIndex(n);
    }

    function shut() {
      if (!open) return;
      open = false;
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
      el.classList.remove('is-open');
      document.documentElement.classList.remove('sz-noscroll');
      if (opener && opener.focus) opener.focus();
    }

    // Anything that is not an arrow closes it — the backdrop, the margin
    // around the photograph, the photograph itself and the close button.
    el.addEventListener('click', function (e) {
      var step = e.target.closest('[data-lightbox-step]');
      if (step) { move(getIndex() + (parseInt(step.getAttribute('data-lightbox-step'), 10) || 0)); return; }
      shut();
    });

    document.addEventListener('keydown', function (e) {
      if (!open) return;
      if (e.key === 'Escape') { shut(); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); move(getIndex() + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); move(getIndex() - 1); }
      // A dialog that hands focus back to the page behind it is not a dialog.
      if (e.key === 'Tab') {
        var stops = $$('button', el).filter(function (b) { return b.offsetParent !== null; });
        if (!stops.length) return;
        var first = stops[0], last = stops[stops.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    (function swipe() {
      var x0 = null;
      el.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse') x0 = e.clientX; }, { passive: true });
      el.addEventListener('pointerup', function (e) {
        if (x0 === null) return;
        var dx = e.clientX - x0; x0 = null;
        if (Math.abs(dx) > 48) move(getIndex() + (dx < 0 ? 1 : -1));
      }, { passive: true });
    })();

    return {
      open: function (i) {
        opener = document.activeElement;
        open = true;
        el.hidden = false;
        el.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('sz-noscroll');
        requestAnimationFrame(function () { el.classList.add('is-open'); });
        paint(i);
        var close = $('[data-lightbox-close]', el);
        if (close) close.focus();
      },
      paint: paint,
      isOpen: function () { return open; }
    };
  }

  /* ---------------------------------------------------- the page stepper */
  // A page count is a number. Rendering forty of them as swatches is a wall,
  // so the radios stay in the DOM — they are what the variant picker reads and
  // what the form submits — and this drives them with a −/+ pair.
  //
  // Which page counts exist depends on the size: an A4 magazine goes up in
  // fours, an A5 in eights. So the list is recomputed from the variant data
  // every time, and a size change re-clamps the count to one that exists.

  function moneyFormatter(fmt) {
    fmt = fmt || '{{amount}}';
    function grouped(v, dec) {
      var parts = v.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    return function (cents) {
      var n = (cents || 0) / 100;
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, key) {
        if (key === 'amount_no_decimals') return grouped(n, 0);
        if (key === 'amount_with_comma_separator') return grouped(n, 2).replace(/,/g, '.').replace(/\.(\d\d)$/, ',$1');
        if (key === 'amount_no_decimals_with_comma_separator') return grouped(n, 0).replace(/,/g, '.');
        if (key === 'amount_with_apostrophe_separator') return grouped(n, 2).replace(/,/g, "'");
        return grouped(n, 2);
      });
    };
  }

  function initSteppers(root) {
    $$('[data-pdp]', root).forEach(function (pdp) {
      var box = $('[data-stepped]', pdp);
      var chips = $('[data-options]', pdp);
      var dataEl = $('[data-variant-data]', pdp) || $('[data-variant-data]');
      if (!box || !chips || !dataEl) return;
      if (box.dataset.szBound) return;
      box.dataset.szBound = '1';

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
      if (!variants || !variants.length) return;

      var row = $('[data-stepped-values]', box);
      var stepper = $('[data-stepper]', box);
      var out = $('[data-page-value]', box);
      var note = $('[data-page-note]', box);
      var radios = row ? $$('input[data-option]', row) : [];
      if (!row || !stepper || !radios.length) return;

      var idx = parseInt(radios[0].getAttribute('data-option'), 10);
      var money = moneyFormatter(chips.getAttribute('data-money-format'));
      var minus = $('[data-page="-1"]', stepper);
      var plus = $('[data-page="1"]', stepper);
      var syncing = false;

      row.hidden = true;
      stepper.hidden = false;

      function count(v) { return parseFloat(v.options[idx]) || 0; }

      // Every variant that agrees with the other options as they stand.
      function lane() {
        var fixed = $$('input[data-option]', chips).filter(function (i) {
          return i.checked && parseInt(i.getAttribute('data-option'), 10) !== idx;
        });
        return variants.filter(function (v) {
          for (var k = 0; k < fixed.length; k++) {
            var at = parseInt(fixed[k].getAttribute('data-option'), 10);
            if (v.options[at] !== fixed[k].value) return false;
          }
          return true;
        }).sort(function (a, b) { return count(a) - count(b); });
      }

      function chosen() {
        for (var i = 0; i < radios.length; i++) if (radios[i].checked) return radios[i].value;
        return radios[0].value;
      }

      function pick(value) {
        var hit = null;
        radios.forEach(function (r) {
          r.checked = r.value === value;
          if (r.checked) hit = r;
        });
        return hit;
      }

      function paint(list, at) {
        if (out) out.textContent = list[at].options[idx];
        if (minus) minus.disabled = at === 0;
        if (plus) plus.disabled = at === list.length - 1;

        if (note) {
          var first = list[0];
          var bits = [first.options[idx] + ' included'];
          if (list.length > 1) {
            var d = count(list[1]) - count(first);
            var money_d = list[1].cents - first.cents;
            if (money_d > 0) {
              bits.push('+' + money(money_d) + (d === 1 ? ' a page' : ' for every ' + d));
            }
          }
          note.textContent = bits.join(' · ');
          note.hidden = false;
        }
      }

      function settle(move, restart) {
        var list = lane();
        if (!list.length) return;

        var at;

        // A size does not inherit the other size's page count: an A5 magazine
        // begins at sixteen pages and an A4 at eight, because that is where
        // each one is actually made. So a size change opens its own lane at
        // its smallest count rather than carrying a number across.
        if (restart) {
          at = 0;
        } else {
          var want = chosen();
          at = -1;
          for (var i = 0; i < list.length; i++) if (list[i].options[idx] === want) { at = i; break; }

          // Landing on a variant whose count this size doesn't offer — a
          // ?variant= link, say — falls to the nearest one it does.
          if (at === -1) {
            var target = parseFloat(want) || 0, best = 0, gap = Infinity;
            for (var j = 0; j < list.length; j++) {
              var g = Math.abs(count(list[j]) - target);
              if (g < gap) { gap = g; best = j; }
            }
            at = best;
          }
        }

        at = clamp(at + (move || 0), 0, list.length - 1);
        paint(list, at);

        if (list[at].options[idx] !== chosen()) {
          // The variant picker only listens for a change whose target is one
          // of the radios — firing it on the container is ignored, and the
          // price silently stops following the stepper.
          var hit = pick(list[at].options[idx]);
          if (hit) {
            syncing = true;
            hit.dispatchEvent(new Event('change', { bubbles: true }));
            syncing = false;
          }
        }
      }

      stepper.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-page]') : null;
        if (!btn || btn.disabled) return;
        settle(parseInt(btn.getAttribute('data-page'), 10));
      });

      // A different size means a different set of page counts, starting again
      // from that size's own minimum.
      chips.addEventListener('change', function (e) {
        if (syncing) return;
        if (e.target && parseInt(e.target.getAttribute('data-option'), 10) === idx) return;
        settle(0, true);
      });

      settle(0);
    });
  }

  /* --------------------------------------------------------------- the chat

     The panel talks to the Worker in chat-worker/, which holds the API key.
     Nothing here knows any secret; the worst a reader of this file learns is
     the endpoint, which is rate-limited and origin-checked at the other end.

     Two things it does that a generic widget does not:

       - it performs cart actions the assistant asks for, so a book agreed in
         conversation lands in the real cart with the brief attached to it;
       - it keeps the transcript in sessionStorage, so navigating from the
         collection to a product does not throw away what was just said. Per
         tab, and gone when the tab closes.
  */

  /* ------------------------------------------------------------ hero sound

     No browser will autoplay something audible, so the clip starts muted and
     the button hands the choice back. Nothing here tries to defeat that: an
     autoplay that gets blocked leaves a silent still frame and a customer
     wondering what broke.

     The button stays hidden unless the file actually carries an audio track.
     Offering to unmute a silent video is a promise of nothing, and the check
     is cheap. */

  /* ---------------------------------------------------------------- reels

     Each tile is the reel's cover with a play button over it. Instagram's
     embed autoplays as soon as it is on screen, so it is not on the page at
     all until someone presses: the press swaps the button for the iframe, and
     because the press is the gesture, the clip starts on its own from there. */
  var playing = null;                    // { btn, frame } — only ever one

  function stopReel() {
    if (!playing) return;
    // Removing the iframe is the only way to stop it: the player lives on
    // instagram.com and there is nothing on this side to call pause() on.
    if (playing.frame.parentNode) playing.frame.parentNode.removeChild(playing.frame);
    playing.btn.hidden = false;
    playing = null;
  }

  function initReels(root) {
    var covers = (root || document).querySelectorAll('[data-reel-embed]');
    for (var i = 0; i < covers.length; i++) {
      (function (btn) {
        if (btn.dataset.reelBound) return;
        btn.dataset.reelBound = '1';
        btn.addEventListener('click', function () {
          stopReel();                      // one at a time, always
          var frame = document.createElement('iframe');
          frame.src = btn.getAttribute('data-reel-embed');
          frame.title = btn.getAttribute('aria-label') || 'Instagram reel';
          frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; clipboard-write');
          frame.setAttribute('allowfullscreen', '');
          frame.setAttribute('scrolling', 'no');
          frame.setAttribute('frameborder', '0');
          btn.parentNode.appendChild(frame);
          btn.hidden = true;
          playing = { btn: btn, frame: frame };
        });
      })(covers[i]);
    }
  }

  function initHeroSound(root) {
    var video = $('[data-hero-video]', root || document);
    var btn = $('[data-hero-sound]', root || document);
    if (!video || !btn) return;

    function hasAudio() {
      // Each browser reports this its own way, and Safari reports none of them
      // until enough of the file has arrived to know.
      if (typeof video.mozHasAudio === 'boolean') return video.mozHasAudio;
      if (typeof video.webkitAudioDecodedByteCount === 'number') {
        return video.webkitAudioDecodedByteCount > 0;
      }
      if (video.audioTracks) return video.audioTracks.length > 0;
      return null;                       // cannot tell yet
    }

    function reveal() {
      var audio = hasAudio();
      // null means the browser will not say — show the button rather than hide
      // a working control on a guess.
      btn.hidden = audio === false;
    }

    if (video.readyState >= 1) reveal();
    video.addEventListener('loadedmetadata', reveal);
    // Safari only decodes audio once playback is under way.
    video.addEventListener('playing', reveal);

    btn.addEventListener('click', function () {
      var muted = !video.muted;
      video.muted = muted;
      btn.setAttribute('aria-pressed', String(!muted));
      btn.setAttribute('aria-label', muted ? 'Turn sound on' : 'Turn sound off');

      // Unmuting can itself be refused if the clip is not already playing.
      if (!muted) {
        var p = video.play();
        if (p && p.catch) p.catch(function () { video.muted = true; btn.setAttribute('aria-pressed', 'false'); });
      }
    });
  }

  function initChat(root) {
    var wrap = $('[data-chat]', root || document);
    if (!wrap || wrap.dataset.ready) return;

    var endpoint = wrap.dataset.endpoint;
    if (!endpoint) return;
    wrap.dataset.ready = '1';

    var panel   = $('[data-chat-panel]', wrap);
    var log     = $('[data-chat-log]', wrap);
    var form    = $('[data-chat-form]', wrap);
    var input   = $('[data-chat-input]', wrap);
    var send    = $('[data-chat-send]', wrap);
    var chips   = $('[data-chat-chips]', wrap);
    var opener  = $('[data-chat-open]', wrap);
    var closer  = $('[data-chat-close]', wrap);

    var KEY = 'sz-chat';
    var history = [];
    var conversationId = null;
    var busy = false;

    /* ------------------------------------------------------------- memory */

    try {
      var saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (saved && Array.isArray(saved.messages)) {
        history = saved.messages;
        conversationId = saved.id || null;
      }
    } catch (e) { /* private mode, or nonsense in storage — start fresh */ }

    function remember() {
      try {
        sessionStorage.setItem(KEY, JSON.stringify({
          id: conversationId,
          // Keep the tail. A whole afternoon of conversation is not worth the
          // quota, and the server trims to the same window anyway.
          messages: history.slice(-40)
        }));
      } catch (e) { /* over quota or blocked — the chat still works */ }
    }

    /* ------------------------------------------------------------ drawing */

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }

    function scroll() { log.scrollTop = log.scrollHeight; }

    function bubble(role, text) {
      var cls = role === 'user' ? 'sz-chat__msg sz-chat__msg--me'
              : role === 'system' ? 'sz-chat__msg sz-chat__msg--sys'
              : 'sz-chat__msg sz-chat__msg--bot';
      var node = el('div', cls, text);
      log.appendChild(node);
      scroll();
      return node;
    }

    function typing() {
      var node = el('div', 'sz-chat__msg sz-chat__msg--bot');
      var dots = el('span', 'sz-chat__typing');
      dots.appendChild(el('i')); dots.appendChild(el('i')); dots.appendChild(el('i'));
      node.appendChild(dots);
      node.setAttribute('aria-label', 'Typing');
      log.appendChild(node);
      scroll();
      return node;
    }

    /* The cart card. Rendered rather than described, because "it's in your
       cart" with no way to get there is a dead end. */
    function cartCard(detail) {
      var card = el('div', 'sz-chat__cart');
      card.appendChild(el('b', null, detail.title));
      card.appendChild(el('span', null, detail.note || 'Added to your cart · free shipping'));
      var a = el('a', null, 'Go to cart');
      a.href = '/cart';
      card.appendChild(a);
      log.appendChild(card);
      scroll();
    }

    /* A handover. Deliberately a link the customer taps rather than a window
       opened for them: a browser blocks window.open outside a click, and on a
       phone wa.me leaves the store entirely, which should be their choice. */
    function whatsappCard(action) {
      var card = el('div', 'sz-chat__cart');
      card.appendChild(el('b', null, 'Talk to the team on WhatsApp'));
      card.appendChild(el('span', null, 'Your message is ready — you can edit it before sending.'));

      var a = el('a', null, action.label || 'Continue on WhatsApp');
      a.href = action.url;
      a.target = '_blank';
      a.rel = 'noopener';
      card.appendChild(a);

      log.appendChild(card);
      scroll();
    }

    /* ------------------------------------------------------------ opening */

    function paint() {
      log.textContent = '';
      if (!history.length) {
        var greeting = wrap.dataset.greeting;
        if (greeting) bubble('assistant', greeting);
        return;
      }
      history.forEach(function (m) { bubble(m.role, m.content); });
    }

    var painted = false;

    function open() {
      if (!painted) { paint(); painted = true; }
      panel.hidden = false;

      // Reading a layout property flushes the style change, so the panel is
      // genuinely displayed before the class lands. Safari otherwise batches
      // both into one frame and skips the entrance animation entirely.
      void panel.offsetHeight;

      wrap.classList.add('is-open');
      opener.setAttribute('aria-expanded', 'true');
      // Not on phones — the keyboard springing up over the greeting before
      // anyone has read it is worse than one extra tap.
      if (window.innerWidth > 540) input.focus();
      scroll();
    }

    function close() {
      panel.hidden = true;
      wrap.classList.remove('is-open');
      opener.setAttribute('aria-expanded', 'false');
      opener.focus();
    }

    opener.addEventListener('click', open);
    closer.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) close();
    });

    /* Any link to #chat opens it — so the CTA sections and the brief form can
       point at the assistant without knowing anything about it. */
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href="#chat"], [data-chat-trigger]');
      if (!a) return;
      e.preventDefault();
      open();
    });

    /* --------------------------------------------------------- composing */

    // Grow with the text, up to the CSS max-height.
    function resize() {
      input.style.height = 'auto';
      input.style.height = Math.min(input.scrollHeight, 118) + 'px';
    }
    input.addEventListener('input', resize);

    // Enter sends, Shift+Enter is a newline — what everyone now expects.
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        form.requestSubmit ? form.requestSubmit() : send.click();
      }
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      ask(input.value);
    });

    if (chips) {
      chips.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-chat-chip]');
        if (chip) ask(chip.textContent.trim());
      });
    }

    /* ------------------------------------------------------------ sending */

    function ask(text) {
      text = (text || '').trim();
      if (!text || busy) return;

      // The chips are openers. Once the conversation has started they are
      // just clutter.
      if (chips) chips.hidden = true;

      input.value = '';
      resize();
      bubble('user', text);
      history.push({ role: 'user', content: text });
      remember();

      busy = true;
      send.disabled = true;
      var dots = typing();

      fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ conversationId: conversationId, messages: history })
      })
        .then(function (r) { return r.json().catch(function () { return {}; }); })
        .then(function (data) {
          dots.remove();

          if (data.conversationId) conversationId = data.conversationId;

          var reply = data.reply || "Sorry — I didn't catch that. Try again?";
          bubble('assistant', reply);
          history.push({ role: 'assistant', content: reply });

          (data.actions || []).forEach(perform);

          if (data.escalated) {
            bubble('system', 'Passed to the team · +91 91132 93431 · Mon–Sat, 10–7');
          }

          remember();
        })
        .catch(function () {
          dots.remove();
          bubble('assistant',
            'I could not reach our side just then — the connection may have dropped. ' +
            'Try again, or call the team on +91 91132 93431.');
        })
        .then(function () {
          busy = false;
          send.disabled = false;
          if (window.innerWidth > 540) input.focus();
        });
    }

    /* ------------------------------------------------------------ actions

       The Worker never touches the cart itself — it has no session and no
       business holding one. It describes what should happen and the page,
       which is logged in as the shopper, does it. */

    function perform(action) {
      if (!action) return;

      if (action.type === 'open_whatsapp') { whatsappCard(action); return; }
      if (action.type !== 'add_to_cart') return;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          id: action.variant_id,
          quantity: action.quantity || 1,
          properties: action.properties || {}
        })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('cart rejected it');
          return r.json();
        })
        .then(function (item) {
          cartCard({
            title: item.product_title + (item.variant_title ? ' · ' + item.variant_title : ''),
            note: 'In your cart · free shipping · nothing prints until you approve the proof'
          });
          // The header count is rendered server-side, so nudge whatever is
          // listening rather than reaching into the header from here.
          document.dispatchEvent(new CustomEvent('sz:cart-updated'));
        })
        .catch(function () {
          bubble('system', 'That did not reach the cart — open the product page and add it there.');
        });
    }
  }

  /* ----------------------------------------------------------------- boot */

  // On Shopify each section renders its own .sz wrapper, so there can be
  // several. Everything below queries the document and works either way.
  function init() {
    // Chrome first — it exists on every page, including ones with no sections.
    initHeader();
    initQty(document);
    initCart(document);

    var scopes = $$('[data-sz]');
    if (!scopes.length) { onScroll(); return; }

    scopes.forEach(function (s) { s.classList.add('sz-ready'); });
    initReveals(document);
    initParallax(document);
    initTilt(document);
    initForm(document);
    initSocial(document);
    initFlipbook(document);
    initGallery(document);
    initRibbon(document);
    initVariants(document);
    initSteppers(document);
    initSort(document);
    initDock(document);
    initQuiz(document);
    initChat(document);
    initHeroSound(document);
    initReels(document);
    onScroll();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', remeasure);
  window.addEventListener('load', remeasure);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Shopify theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (e) {
    readers.length = 0;
    measurers.length = 0;
    init(document);
  });
})();
